// =========================================================================
// HelpyDoc Metabolic · Photo Storage — acceso a los objetos del bucket.
//
// Este módulo YA NO SUBE. La subida entera pasa por el backend
// (`ApiClient.uploadPhoto` → POST /photos/upload-url → PUT a la URL firmada),
// que es lo que garantiza que toda foto tenga su documento de metadatos.
//
// POR QUÉ. Hasta 2026-08-04 convivían DOS circuitos y la misma foto se subía
// por los dos en el mismo gesto del paciente:
//   1. `uploadTempMealPhoto()` — SDK directo, sin documento de metadatos y sin
//      comprobar el consentimiento (las reglas solo miran el uid).
//   2. URL firmada del backend — con documento y con consentimiento.
// Resultado: dos objetos por comida, uno de ellos con dato de salud y sin nada
// en Firestore que lo referenciara, escrito además aunque el consentimiento de
// imagen estuviera revocado. El circuito 1 se ha eliminado.
//
// Aquí queda lo que sigue necesitando el SDK del navegador: RESOLVER la URL de
// visualización a partir de la ruta canónica, y BORRAR.
// =========================================================================
// El SDK de Storage NO se importa de forma estática: entraba en el bundle de
// ARRANQUE de toda la app, y este módulo lo alcanza `data-adapter`, que es
// núcleo. Resultado: quien solo consultaba su diario pagaba la descarga del SDK
// de subida de ficheros sin tocar una foto. Se carga bajo demanda, el mismo
// patrón que ya usa `lib/platos-imagenes.ts`.
type SdkStorage = typeof import('firebase/storage');
let _sdk: Promise<SdkStorage> | null = null;
function sdkStorage(): Promise<SdkStorage> {
  _sdk ??= import('firebase/storage');
  return _sdk;
}

/**
 * URL de visualización a partir de la ruta canónica.
 *
 * `storagePath` es lo que se guarda; la URL se resuelve cuando hace falta pintar
 * la imagen. Al revés —guardar la URL y deducir la ruta— es lo que había antes,
 * y por eso el borrado no encontraba el objeto: la URL caduca, lleva un token y
 * depende del bucket; la ruta no.
 */
export async function downloadUrlForPath(storagePath: string): Promise<string> {
  const { getStorage, ref, getDownloadURL } = await sdkStorage();
  return getDownloadURL(ref(getStorage(), storagePath));
}

/**
 * Borra una foto por su ruta completa de Storage (silencioso si no existe).
 * Usado para limpiar fotos tomadas pero descartadas.
 */
export async function deleteMealPhotoByPath(path: string): Promise<void> {
  try {
    const { getStorage, ref, deleteObject } = await sdkStorage();
    const storage = getStorage();
    await deleteObject(ref(storage, path));
  } catch {
    // Ya borrada o nunca subida — OK.
  }
}

/**
 * Ruta del objeto en Storage a partir de la URL que se guardó con la comida.
 *
 * POR QUÉ EXISTE. La ruta real del objeto NO se puede reconstruir a partir del
 * `mealId`, por dos motivos independientes:
 *
 *   • EL NOMBRE. Las fotos que sube `uploadTempMealPhoto()` se llaman por marca
 *     de tiempo (`{Date.now()}.{ext}`), no por comida — el mealId todavía no
 *     existe cuando se suben. `{mealId}.{ext}` no puede coincidir jamás.
 *   • LA EXTENSIÓN. Depende del tipo REAL de los bytes, y ese tipo NO está
 *     garantizado: la normalización a WebP (`lib/norma-imagen.ts`) devuelve el
 *     data URL ORIGINAL si el navegador no produce WebP o si la foto ya era
 *     pequeña. Sale `.jpg` tanto como `.webp`.
 *
 * Medido en el bucket de producción el 2026-08-04: de 28 objetos bajo
 * `users/{uid}/photos/`, 12 con nombre de marca de tiempo y 16 con uuid, y
 * NINGUNO en WebP pese a que la norma lo pide. Los 12 primeros son exactamente
 * los que el borrado antiguo no pudo tocar nunca.
 *
 * La URL guardada es lo ÚNICO que apunta al objeto, así que es ella la que manda
 * a la hora de borrarlo.
 *
 * Formatos aceptados:
 *   • URL de descarga de Firebase — `.../v0/b/{bucket}/o/{ruta%2Fcodificada}?...`
 *   • `gs://{bucket}/{ruta}`
 * Cualquier otra cosa (una `data:` URL del modo demo, una cadena vacía) → null.
 */
export function storagePathFromUrl(url: string | null | undefined): string | null {
  if (!url) return null;

  if (url.startsWith('gs://')) {
    const barra = url.indexOf('/', 'gs://'.length);
    if (barra === -1) return null;
    return url.slice(barra + 1) || null;
  }

  if (!/^https?:\/\//i.test(url)) return null;
  try {
    const { pathname } = new URL(url);
    const marca = '/o/';
    const i = pathname.indexOf(marca);
    if (i === -1) return null;
    // `pathname` conserva el %2F de la ruta codificada; decodificarlo devuelve
    // la ruta con sus barras: users/{uid}/photos/{objeto}.{ext}
    const codificada = pathname.slice(i + marca.length);
    return codificada ? decodeURIComponent(codificada) : null;
  } catch {
    return null;
  }
}

/**
 * Borra la foto a la que apunta una URL guardada. Devuelve `false` si la URL no
 * identifica ningún objeto de Storage (modo demo, URL vacía): en ese caso no hay
 * nada que borrar y el llamador no debe darlo por hecho.
 */
export async function deleteMealPhotoByUrl(url: string | null | undefined): Promise<boolean> {
  const path = storagePathFromUrl(url);
  if (!path) return false;
  await deleteMealPhotoByPath(path);
  return true;
}

/**
 * Extensiones con las que puede haberse nombrado un objeto de foto.
 *
 * `jpeg` está porque el circuito de URL firmada deriva el nombre del
 * `contentType` (`image/jpeg` → `.jpeg`), no de la tabla de tres valores del
 * SDK. En el bucket de producción conviven las dos formas: 16 objetos `.jpeg` y
 * 12 `.jpg` (medido el 2026-08-04). Omitirla dejaría fuera del respaldo justo a
 * los más numerosos.
 */
const EXTENSIONES_POSIBLES = ['webp', 'jpg', 'jpeg', 'png'] as const;

/**
 * Borra la foto de una comida.
 *
 * `fotoUrl` es el camino bueno: identifica el objeto exacto. El borrado por
 * `uid + mealId` es solo el respaldo para fotos antiguas subidas con
 * `uploadMealPhoto()` (que sí nombra el objeto por mealId) y de las que ya no
 * conservamos la URL.
 *
 * EL FALLO QUE ARREGLA: antes esto era una sola línea que clavaba `.jpg`. Pero
 * el cliente normaliza a WebP (`lib/norma-imagen.ts`, FORMATO = 'image/webp') y
 * la subida nombra el objeto por su tipo REAL, así que la ruta construida no
 * existía nunca. `deleteObject` fallaba, el catch se lo tragaba, y la foto de la
 * comida borrada se quedaba en el bucket para siempre — sin ningún documento que
 * la referenciara. Un borrado que no borra y además no se queja es lo peor de
 * los dos mundos.
 */
export async function deleteMealPhoto(
  uid: string,
  mealId: string,
  fotoUrl?: string | null,
): Promise<void> {
  if (await deleteMealPhotoByUrl(fotoUrl)) return;

  // Sin URL utilizable: se prueban todas las extensiones que la subida pudo
  // generar. Borrar es idempotente y las que no existan fallan en silencio.
  await Promise.all(
    EXTENSIONES_POSIBLES.map((ext) => deleteMealPhotoByPath(`users/${uid}/photos/${mealId}.${ext}`)),
  );
}
